import React, { useState, useEffect, useCallback, useRef  } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, elements, registerables } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css';
import { url } from '../../../../../api/endpointURL';
import { selectedBarBorderColor, barBackgroundColorLineCrossing, barBackgroundColorReverseLineCrossing } from '../../../../../config/index';

Chart.register(...registerables);


//returns current week numbner
function getCurrentWeekNumber() {
    const currentDate = new Date();
    const startOfYear = new Date(currentDate.getFullYear(), 0, 0);
    const diff = currentDate - startOfYear;
    return Math.floor(diff / (7 * 24 * 60 * 60 * 1000));
}

const DetectChart = ({ hwid, site, selectedCamera, selectedCameraId, selectedYear }) => {

    //might need to come back here to check if it is getting the right month and when I confirm that we are just using calendar year instead of financial yeat. 
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [monthSelectedByUser, setMonthSelectedByUser] = useState(false);
    const [detectionData, setDetectionData] = useState([]);
    const [detectionDataLoading, setDetectionDataLoading] = useState(false);
    const [selectedPolygon, setSelectedPolygon] = useState();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const chartRef = useRef(null);

    const smallScreen = window.innerWidth < 786;


    // Make a function that compares this month's peak vs the previous month's peak.
    const comparePeakToPreviousMonth = () => {
        if (detectionData.length < 2) return "No previous data available.";

        const currentMonthData = detectionData.reduce((max, d) => 
            d.peak_detection > max.peak_detection ? d : max, detectionData[0]);
    
        const previousMonthNumber = month === 1 ? 12 : month - 1;
        let previousMonthData = detectionData.find(d => d.month === previousMonthNumber);
        previousMonthData = detectionData.reduce((max, d) => 
            d.peak_detection > max.peak_detection ? d : max, detectionData[0]);

    
        if (!previousMonthData) return "no data";
    
        const difference = currentMonthData.peak_detection - previousMonthData.peak_detection;
        const percentChange = ((difference / previousMonthData.peak_detection) * 100).toFixed(2);
    
        if (difference > 0) {
            return `an increase max of ${Math.abs(difference)} (${percentChange}%) in ${previousMonthData.polygon_name} `;
        } else if (difference < 0) {
            return `a decrease max of ${Math.abs(difference)} (${percentChange}%) in ${previousMonthData.polygon_name} `;
        } else {
            return `the same as the peak in ${previousMonthData.polygon_name} as `;
        }
    };

    const getPeakDetection = () => {
        if (!detectionData.length) return { peak: 0, region: "N/A" };

        const peakData = detectionData.reduce((max, d) => 
            d.peak_detection > max.peak_detection ? d : max, detectionData[0]);

        return { peak: peakData.peak_detection, region: peakData.polygon_name };
    }

    const getMonthName = (monthNumber) => {
        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        return months[monthNumber - 1];
    };

    const useEffectOnMonthChange = () => {
        if (monthSelectedByUser) {
            fetchPeakDetectionData(month);
        }
    };

    const fetchPeakDetectionData = useCallback(async (monthNumber) => {
        try {
            // I should rename this to something else. Find a name that better describes the table. This will change when the table changes so for now it doesn't matter. 
            setDetectionDataLoading(true);
            const response = await fetch(`${url}/detect?year=${selectedYear}&month=${monthNumber}&hwid=${hwid}`);
            const responseData = await response.json();
            setDetectionDataLoading(false);

            // console.log("data fetched for peak detections for the month is: ", responseData);
            setDetectionData(responseData);
            // console.log("detectionData: ", detectionData);

            if (responseData.length) {
                const indexLastMonth = responseData.length - 1;
                setMonth(responseData[indexLastMonth]?.month);
            }


        } catch (error) {
            console.error('Error fetching monthly detection data: ', error);
            setDetectionDataLoading(false);
        }
    }, [selectedYear, hwid]);

    // Chart,js peak_detections preparation
    const preparePeakDetectionsChartData = () => {

        return {
        labels: detectionData.map(d => d.polygon_name),
        datasets: [
            {
                label: 'Peak Detection',
                data: detectionData.map(d => d.peak_detection),
                backgroundColor: '#64C6E6',
                borderColor: '#17a2b8',
                borderWidth: 1,
                borderRadius: 10,
            }
        ],
        };
    };

    // Need to make functions that will handle when polygons are clicked if that is something I want users to be able to do. 

    useEffect(() => {
        if (selectedYear && hwid) {
            fetchPeakDetectionData(month);
        }
    }, [selectedYear, hwid, fetchPeakDetectionData]);

    // Set selectedPolygon as the one at the end of the list when data first loaded.
    useEffect(() => {
        if (!selectedPolygon && detectionData.length) {
            const lastPolygonIndex = detectionData.length -1;
            setSelectedPolygon(detectionData[lastPolygonIndex].polygon_name);
        }
    }, [detectionData, selectedPolygon]);

    useEffect(() => {
        const escHandler = (e) => {
            if (e.key === 'Escape') setIsFullscreen(false);
        };
        window.addEventListener('keydown', escHandler);
        return () => window.removeEventListener('keydown', escHandler);
        }, []);

    return (
        <div className='graphs-container'>
            <div id='detect-bar-chart-container'>
                <div className={`detect-bar-chart-wrapper ${isFullscreen ? 'fullscreen-detect-chart' : ''}`}>
                    {/* Fullscreen Toggle Button */}
                    <button
                        className="fullscreen-toggle-btn"
                        onClick={(e) => {
                        e.stopPropagation(); // Avoid interfering with chart clicks
                        setIsFullscreen(prev => !prev);
                        }}
                    >
                        {isFullscreen ? 'Close' : 'Fullscreen'}
                    </button>
                    <div style={{ display: 'flex', justifyContent: 'space-between'}}>
                        <div className='graph-title' style={{ marginBottom: '10px' }}>
                            Base Detections
                        </div>
                    </div>
                    <div className={ detectionDataLoading ? 'detect-bar-chart-container-loading' : ''}>
                        <Bar
                            data={preparePeakDetectionsChartData()}
                            options={{
                                responsive: true, //Ensure the chart is responsive
                                maintainAspectRatio: true,
                                scales: {
                                    x: {
                                        title: {
                                            display: true,
                                            text: 'Polygon Names',
                                        },
                                        stacked: true,
                                    },
                                    y: {
                                        title: {
                                            display: true,
                                            text: 'Peak People Detected',
                                        },
                                        stacked: true,
                                    },
                                },
                                plugins: {
                                    tooltip: {
                                        mode: 'nearest',
                                        intersect: false,
                                    },
                                    legend: {
                                        position: 'top',
                                        labels: {
                                            font: {
                                                size: smallScreen ? 8 : 14,
                                            },
                                            boxWidth: smallScreen ? 8 : 40,
                                            boxHeight: smallScreen ? 8 : 10,
                                        },
                                    },
                                },
                                onClick: (event, elements) => {
                                    if (elements.length) {
                                        const index = elements[0].index;

                                    }
                                },
                            }}
                        />
                        <div className='count-chart-footer-container' style={{ textAlign: 'center' }}>
                            <div className='count-chart-footer-value'>{getPeakDetection().peak} in {getPeakDetection().region}</div>
                            <div className='count-chart-footer-label'>This is {comparePeakToPreviousMonth()} last month.</div>
                        </div>
                        {
                            detectionDataLoading && <div id='detect-loading-overlay'>
                                <BeatLoader color={'white'} loading={detectionDataLoading} size={15} />
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetectChart;